import { getColorWithOpacity, SEMANTIC_COLORS } from '@/constants/colors.constant'
import {
  WHAT_IF_PORTFOLIO_RANGE_OPTIONS,
  WhatIfPortfolioRangeKey,
} from '@/services/whatIfAnalysis.service'
import { themeClassName } from '@/utils/grid-utils/ag-grid-config'
import { validateInput } from '@/utils/UserInputValidation'
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model'
import { ColDef, GridReadyEvent } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import { Search } from '@mui/icons-material'
import { Box, MenuItem, Select, SelectChangeEvent, TextField, Typography } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

interface Portfolio {
  id: string
  name: string
  date: string
  currency: string
  benchmarkDescription: string
  numOfSecurities: number
  uniqueId?: string
  originalId?: string
}

interface PortfolioTableProps {
  portfolios: Portfolio[]
  selectedPortfolio: Portfolio | null
  onPortfolioSelect: (portfolio: Portfolio | null) => void
  onNotification?: (message: string) => void
  loading?: boolean
  portfolioRange?: WhatIfPortfolioRangeKey
  onPortfolioRangeChange?: (range: WhatIfPortfolioRangeKey) => void
  error?: string | null
}


const PortfolioTable: React.FC<PortfolioTableProps> = ({
  portfolios,
  selectedPortfolio,
  onPortfolioSelect,
  onNotification,
  loading = false,
  portfolioRange,
  onPortfolioRangeChange,
  error
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [gridApi, setGridApi] = useState<any>(null)

  // Handle row click to select portfolio
  const onRowClicked = useCallback((event: any) => {
    onPortfolioSelect(event.data)
  }, [onPortfolioSelect])

  // Column definitions
  const columnDefs: ColDef[] = useMemo(() => [
    {
      headerName: 'Portfolio',
      field: 'name',
      flex: 2,
      minWidth: 150,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'startsWith', 'endsWith'],
        defaultOption: 'contains'
      },
      cellStyle: (params) => {
        const isSelected = selectedPortfolio?.uniqueId === params.data?.uniqueId
        return {
          cursor: 'pointer',
          backgroundColor: isSelected ? getColorWithOpacity(SEMANTIC_COLORS.SELECTION_BG, 0.3) : 'transparent',
          fontWeight: isSelected ? 'bold' : 'normal',
          color: isSelected ? SEMANTIC_COLORS.SELECTION_TEXT : 'inherit'
        }
      }
    },
    {
      headerName: 'Date',
      field: 'date',
      flex: 1,
      minWidth: 100,
      valueFormatter: (params) => params.value ? String(params.value).substring(0, 10) : 'N/A',
      filter: 'agDateColumnFilter',
      cellStyle: (params) => {
        const isSelected = selectedPortfolio?.uniqueId === params.data?.uniqueId
        return {
          backgroundColor: isSelected ? getColorWithOpacity(SEMANTIC_COLORS.SELECTION_BG, 0.3) : 'transparent',
          color: isSelected ? SEMANTIC_COLORS.SELECTION_TEXT : 'inherit',
          textAlign: 'right'
        }
      }
    },
    {
      headerName: 'Currency',
      field: 'currency',
      flex: 1,
      minWidth: 80,
      filter: 'agTextColumnFilter',
      valueFormatter: (params) => {
        if (!params.value || params.value === '') {
          return '---'
        }
        return params.value
      },
      cellStyle: (params) => {
        const isSelected = selectedPortfolio?.uniqueId === params.data?.uniqueId
        return {
          backgroundColor: isSelected ? getColorWithOpacity(SEMANTIC_COLORS.SELECTION_BG, 0.3) : 'transparent',
          color: isSelected ? SEMANTIC_COLORS.SELECTION_TEXT : 'inherit',
          textAlign: 'right'
        }
      }
    },
    {
      headerName: 'Benchmark',
      field: 'benchmarkDescription',
      flex: 2,
      minWidth: 150,
      filter: 'agTextColumnFilter',
      cellStyle: (params) => {
        const isSelected = selectedPortfolio?.uniqueId === params.data?.uniqueId
        return {
          backgroundColor: isSelected ? getColorWithOpacity(SEMANTIC_COLORS.SELECTION_BG, 0.3) : 'transparent',
          color: isSelected ? SEMANTIC_COLORS.SELECTION_TEXT : 'inherit',
          textAlign: 'right'
        }
      }
    },
    {
      headerName: 'Total Securities',
      field: 'numOfSecurities',
      flex: 1,
      minWidth: 100,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => params.value?.toLocaleString() || '0',
      cellStyle: (params) => {
        const isSelected = selectedPortfolio?.uniqueId === params.data?.uniqueId
        return {
          backgroundColor: isSelected ? getColorWithOpacity(SEMANTIC_COLORS.SELECTION_BG, 0.3) : 'transparent',
          color: isSelected ? SEMANTIC_COLORS.SELECTION_TEXT : 'inherit'
        }
      }
    }
  ], [selectedPortfolio])

  // Default column properties
  const defaultColDef: ColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    suppressMenu: false,
    floatingFilter: false
  }), [])

  // Grid options
  const gridOptions = useMemo(() => ({
    suppressRowClickSelection: true,
    suppressCellFocus: true,
    enableCellTextSelection: true,
    animateRows: true,
    pagination: true,
    paginationPageSize: 50,
    paginationPageSizeSelector: [25, 50, 100, 200],
    suppressPaginationPanel: false,
    suppressRowHoverHighlight: false,
    suppressMenuHide: false,
    enableBrowserTooltips: true,
    tooltipShowDelay: 500,
    suppressLoadingOverlay: false,
    suppressNoRowsOverlay: false,
    suppressContextMenu: true
  }), [])

  // Handle grid ready
  const onGridReady = useCallback((params: GridReadyEvent) => {
    setGridApi(params.api)
  }, [])

  // Handle search
  const handleSearch = useCallback((searchValue: string) => {
    // Validate search input - allow letters, numbers, and basic special characters
    if (searchValue.trim()) {
      const validation = validateInput(searchValue.trim(), {
        fieldName: 'Search',
        maxLength: 100,
        allowedPattern: /^[a-zA-Z0-9\s\-_()]+$/,
        blockedChars: ['&', '%', '#', '@', ';', '"', "'", '\\', '/', '*', '<', '>']
      })
      if (!validation.isValid) {
        // Don't update search if invalid, but still clear the filter
        setSearchTerm('')
        if (gridApi) {
          gridApi.setGridOption('quickFilterText', '')
        }
        return
      }
    }
    
    setSearchTerm(searchValue)
    if (gridApi) {
      gridApi.setGridOption('quickFilterText', searchValue)
    }
  }, [gridApi])

  // Row data with unique IDs
  const rowData = useMemo(() => {
    return portfolios.map((portfolio, index) => ({
      ...portfolio,
      uniqueId: `${portfolio.id || portfolio.name}-${index}`, // Create truly unique ID
      originalId: portfolio.id // Keep original ID for reference
    }))
  }, [portfolios])

  // Reset to the first page so the pagination summary reflects the new row count
  // whenever the underlying portfolio list changes (e.g. toggling date range)
  useEffect(() => {
    if (gridApi) {
      gridApi.paginationGoToPage(0)
    }
  }, [portfolios, gridApi])

  // AG Grid's noRowsOverlayComponent closes over `error`, but AG Grid only redraws
  // that overlay on its own rowData/loading transitions - not when error changes
  // while rows stay empty across the switch. Nudge it explicitly so a failed
  // reload actually surfaces the new message instead of leaving a stale overlay.
  useEffect(() => {
    if (gridApi && !loading && rowData.length === 0) {
      gridApi.showNoRowsOverlay()
    }
  }, [gridApi, error, loading, rowData.length])

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      border: '1px solid #e0e0e0',
      borderRadius: 1,
      backgroundColor: 'white'
    }}>
      {/* Header */}
      <Box sx={{ 
        backgroundColor: 'white',
        borderBottom: '1px solid #e0e0e0',
        p: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="h2" sx={{ fontWeight: 'bold', color: '#000000' }}>
          Portfolio(s)
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {onPortfolioRangeChange && portfolioRange && (
            <Select
              data-cy="what-if-portfolio-range-select"
              size="small"
              aria-label="Portfolio date range"
              value={portfolioRange}
              onChange={(e: SelectChangeEvent) =>
                onPortfolioRangeChange(e.target.value as WhatIfPortfolioRangeKey)
              }
              sx={{ minWidth: 120 }}
            >
              {WHAT_IF_PORTFOLIO_RANGE_OPTIONS.map((option) => (
                <MenuItem key={option.key} value={option.key}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          )}
          <TextField
            size="small"
            placeholder="Search portfolios..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            sx={{ minWidth: 250 }}
          />
        </Box>
      </Box>

      {/* AG-Grid */}
      <Box sx={{ flex: 1, width: '100%' }} className={themeClassName}>
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          gridOptions={gridOptions}
          onGridReady={onGridReady}
          onRowClicked={onRowClicked}
          modules={[ClientSideRowModelModule]}
          loading={loading}
          loadingOverlayComponent={() => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography>Loading portfolios...</Typography>
            </Box>
          )}
          noRowsOverlayComponent={() => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', px: 2, textAlign: 'center' }}>
              {error ? (
                <Box>
                  <Typography color="error">{error}</Typography>
                  <Typography color="error" variant="body2">
                    Please try again, reduce the date range, or contact support.
                  </Typography>
                </Box>
              ) : (
                <Typography color="textSecondary">
                  {searchTerm
                    ? `No portfolios found matching "${searchTerm}"`
                    : 'No portfolios found'}
                </Typography>
              )}
            </Box>
          )}
        />
      </Box>
    </Box>
  )
}

export default PortfolioTable
